/**
 * @module app.shared
 */

import { autoinject } from 'aurelia-framework';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { convertToString } from 'app/shared/lib/utilities';
import { signalNames } from 'app/shared/lib/signals';

@autoinject
export class IntegerFormatValueConverter {

    protected signals: string[] = [signalNames.languageChanged];

    constructor(private formatProvider: FormatProvider) { }

    public toView(value: unknown, format?: unknown): string | null {
        if(typeof value === 'string' || typeof value === 'number') {
            return this.formatProvider.formatInteger(value, { format: convertToString(format) });
        }
        return null;
    }

}

@autoinject
export class NumberFormatValueConverter {

    protected signals: string[] = [signalNames.languageChanged];

    constructor(private formatProvider: FormatProvider) { }

    public toView(value: unknown, format?: unknown): string | null {
        if(typeof value === 'string' || typeof value === 'number') {
            return this.formatProvider.formatNumber(value, { format: convertToString(format) });
        }
        return null;
    }

}

@autoinject
export class DecimalFormatValueConverter {

    protected signals: string[] = [signalNames.languageChanged];

    constructor(private formatProvider: FormatProvider) { }

    public toView(value: unknown, format?: unknown): string | null {
        if(typeof value === 'string' || typeof value === 'number') {
            return this.formatProvider.formatDecimal(value, { format: convertToString(format) });
        }
        return null;
    }

}

@autoinject
export class CurrencyFormatValueConverter {

    public signals: string[] = [signalNames.languageChanged];

    constructor(private formatProvider: FormatProvider) { }

    public toView(value: unknown, format?: unknown): string | null {
        if(typeof value === 'string' || typeof value === 'number') {
            return this.formatProvider.formatCurrency(value, { format: convertToString(format) });
        }
        return null;
    }

}

/**
 * **Documentation To Be Provided**
 *
 * @param value A decimal value between 0 and 1
 * @param format
 */
@autoinject
export class PercentFormatValueConverter {

    public signals: string[] = [signalNames.languageChanged];

    constructor(private formatProvider: FormatProvider) { }

    public toView(value: unknown, format?: unknown): string | null {
        if(typeof value === 'string' || typeof value === 'number') {
            return this.formatProvider.formatPercent(value, { format: convertToString(format) });
        }
        return null;
    }
}

export class AsDecimalValueConverter {
    public toView(val: number, digits: number = 0) {
        return typeof (val) === 'number' ? val.toFixed(digits) : val;
    }
    public fromView(val: string | number, digits: number = 0): number {
        return Number(String(val).replace(/[^-\d]/g, '')) / Math.pow(10, digits);
    }
}

export class ClampValueConverter {
    public toView(value: number, max: number = Infinity, min: number = -Infinity): number {
        if(max === null) { max = Infinity; }
        if(min === null) { min = -Infinity; }
        const num = Number(value);
        return !isNaN(num) ? Math.min(Number(max), Math.max(Number(min), num)) : value;
    }
}

export class AsNumberValueConverter {

    private value: number = 0;

    public fromView(val: unknown): number {
        const num = Number(val);
        if(!isNaN(num)) {
            this.value = num;
        }
        return this.value;
    }

}

export class MinValueConverter {
    public fromView(val: number, min: number = -Infinity): number {
        if(min === null) {
            min = -Infinity;
        }
        const num = Number(val);
        return !isNaN(num) ? Math.max(Number(min), num) : val;
    }
}

export class MaxValueConverter {
    public fromView(val: number, max: number = Infinity): number {
        if(max === null) {
            max = Infinity;
        }
        const num = Number(val);
        return !isNaN(num) ? Math.min(Number(max), num) : val;
    }
}
